import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { PMDialog } from "../../utils/PMDialog";
import { ResourceLoading } from "../../utils/ResourceLoading";
import {
  ArrowRightCircleIcon,
  CalendarIcon,
  RectangleGroupIcon,
} from "@heroicons/react/24/outline";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { TWclassNames } from "../../utils/Div";
import { fetchCommuncations } from "../../../api/customer/communcations";
import { MODULE_URL } from "../../Utils";
import { PMPagination } from "../../utils/PMPagination";

export const CommuncationsSection = (props) => {
  const {
    related_type,
    related_id,
    customer,
    type,
    /** When false, only the dialog is rendered; open via `open` + `onOpenChange` (e.g. header More menu). */
    renderTrigger = true,
    open: openProp,
    onOpenChange: onOpenChangeProp,
  } = props;
  const { t } = useTranslation();

  const [internalOpen, setInternalOpen] = useState(false);
  const controlled =
    typeof onOpenChangeProp === "function" && openProp !== undefined;
  const modalOpen = controlled ? openProp : internalOpen;
  const setModalOpen = (next) => {
    if (controlled) onOpenChangeProp(next);
    else setInternalOpen(next);
  };
  const [isLoading, setIsLoading] = useState(true);
  const [communications, setCommuncations] = useState([]);
  const [controller, setController] = useState({
    page: 1,
    per_page: 10,
    sort: "created_at",
    order: "desc",
  });

  const [data, setData] = useState({
    count: 0,
    currentCount: 0,
  });

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const hasAdminAccess = useUserHasAccess({ role: "service", action: "reset" });
  const [error, setError] = useState(null);
  const getCommuncations = async () => {
    await fetchCommuncations({
      customer_id: customer.id,
      related_type,
      related_id,
      controller: {
        ...controller,
      },
    })
      .then((res) => {
        setCommuncations(res?.data?.data);
        setData({
          count: res?.data?.count,
          currentCount: res?.data?.current_count,
        });
        setIsLoading(false);
      })
      .catch(() => {
        setError(t("error.fetching_communcations"));
        setIsLoading(false);
      });
  };

  // Load communcations when dialog opens
  useEffect(() => {
    if (modalOpen && related_type && related_id) {
      getCommuncations();
    }
  }, [modalOpen]);

  const DialogContent = () => {
    if (error) {
      return <div className="text-red-500 text-sm">{error}</div>;
    }

    return (
      <>
        <div className="overflow-hidden bg-white shadow sm:rounded-md">
          <ul role="list" className="divide-y divide-gray-200">
            {communications?.map((item) => (
              <li key={item.id}>
                <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col text-sm font-medium">
                      <span className="text-blue-500 font-semibold capitalize">
                        {item.channel} – {item.direction}
                      </span>
                      <span className="text-black">
                        {item.subject || "(ingen tittel)"}
                      </span>
                    </div>

                    {/* View details button */}
                    <Link
                      to={`/dashboard/customers/${customer?.id}/communications/${item.id}`}
                      className="ml-4 text-sm text-white bg-blue-500 px-3 py-1.5 rounded-md hover:bg-blue-600"
                    >
                      Vis detaljer
                    </Link>
                  </div>

                  <div className="mt-2 sm:flex sm:justify-between">
                    <div className="sm:flex text-sm text-gray-500 items-center">
                      <CalendarIcon
                        className="mr-1.5 h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                      <span>
                        {item.metadata?.service_appointment_reminder_id ? t(`serviceReminders.state.${item.metadata.state}`) : "Sendt"}{" "}
                        <time dateTime={item.sent_at}>{item.sent_at}</time>
                      </span>
                    </div>
                  </div>

                  {item.body_text && (
                    <p className="mt-2 text-sm text-gray-700 line-clamp-3">
                      {item.body_text}
                    </p>
                  )}

                  {item.related?.id && item.related?.displayTitle && (
                    <div className="mt-4">
                      <Link
                        to={MODULE_URL(
                          item.related_type?.toLowerCase(),
                          item.related.id
                        )}
                        target="_blank"
                        className="inline-flex items-center text-blue-500 hover:underline text-sm"
                      >
                        <ArrowRightCircleIcon className="h-4 w-4 mr-1" />
                        {item.related.displayTitle}
                      </Link>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
        <PMPagination
          disabled={isLoading}
          onPageChange={handlePageChange}
          page={controller.page}
          perPage={controller.per_page}
          rowsCount={data?.currentCount}
          totalCount={data?.count}
          currentCount={data?.currentCount}
          showCount={true}
        />
      </>
    );
  };

  let content = null;

  if (hasAdminAccess) {
    if (type === "button") {
      if (renderTrigger) {
        content = (
          <button
            onClick={() => setModalOpen(true)}
            type="button"
            className={TWclassNames(
              "border-blue-400",
              "group flex items-center p-2 border-2 rounded-md"
            )}
          >
            <RectangleGroupIcon
              className="h-4 w-4 text-black group-hover:text-gray-500"
              aria-hidden="true"
            />
            <span className="sr-only">Notes, view notes</span>
          </button>
        );
      }
    } else if (type === "section") {
      content = (
        <div className="col-span-1 lg:col-span-5 row-span-2">
          <div className="flex justify-between items-center bg-white shadow-md rounded-xl px-4 py-4">
            <div className="flex flex-col text-left">
              <span className="text-md font-bold">
                Kommunikasjon knyttet til {customer?.name}
              </span>
              <span className="text-sm italic text-gray-400">
                Dette kan være til hjelp for å forstå sammenhengen
              </span>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setModalOpen(true)}
                type="button"
                className="text-sm text-white bg-blue-400 px-4 py-2 rounded-md"
              >
                Åpne sak i ny fane
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <>
      {content}
      <PMDialog
        title={`Kommunikasjon knyttet til ${customer?.name}`}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      >
        {isLoading ? <ResourceLoading /> : <DialogContent />}
      </PMDialog>
    </>
  );
};
